import type { WeekDayHours, WorkingHours } from '@/hooks/useClinicUsers';
import {
  WORKING_HOURS_DAY_KEYS,
  startOfDay,
  type WorkingHoursDayKey,
} from '@/utils/appointmentCalendarDates';
import { parseHourFromHhmm } from '@/utils/appointmentCalendarStyle';

export type OccupancyLevel = 'off' | 'green' | 'yellow' | 'red';

export type OccupancyAppointment = {
  clinician_id: string | null;
  starts_at: string;
  ends_at: string;
};

export type OccupancyCell = {
  date: Date;
  dayKey: WorkingHoursDayKey;
  level: OccupancyLevel;
  workingMinutes: number;
  bookedMinutes: number;
  ratio: number | null;
};

export function minutesFromHhmm(value: string): number {
  return Math.round(parseHourFromHhmm(value) * 60);
}

export function workingMinutesForDay(hours: WeekDayHours | null | undefined): number {
  if (!hours?.enabled) return 0;
  return Math.max(0, minutesFromHhmm(hours.end) - minutesFromHhmm(hours.start));
}

export function occupancyLevel(workingMinutes: number, bookedMinutes: number): OccupancyLevel {
  if (workingMinutes <= 0) return 'off';
  if (bookedMinutes > workingMinutes) return 'red';
  const ratio = bookedMinutes / workingMinutes;
  if (ratio < 0.5) return 'green';
  if (ratio < 0.9) return 'yellow';
  return 'red';
}

/** Minutes of an appointment that fall on the local calendar day. */
export function bookedMinutesOnDay(
  appointments: OccupancyAppointment[],
  clinicianId: string,
  day: Date,
): number {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  let total = 0;

  for (const apt of appointments) {
    if (apt.clinician_id !== clinicianId) continue;
    const start = new Date(apt.starts_at).getTime();
    const end = new Date(apt.ends_at).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const overlapStart = Math.max(start, dayStart);
    const overlapEnd = Math.min(end, dayEnd);
    if (overlapEnd <= overlapStart) continue;
    total += (overlapEnd - overlapStart) / 60_000;
  }

  return Math.round(total);
}

export function occupancyCellForDay(input: {
  date: Date;
  workingHours: WorkingHours;
  appointments: OccupancyAppointment[];
  clinicianId: string;
}): OccupancyCell {
  const dayKey = WORKING_HOURS_DAY_KEYS[input.date.getDay()] ?? 'monday';
  const workingMinutes = workingMinutesForDay(input.workingHours[dayKey]);
  const bookedMinutes = bookedMinutesOnDay(input.appointments, input.clinicianId, input.date);
  return {
    date: input.date,
    dayKey,
    workingMinutes,
    bookedMinutes,
    ratio: workingMinutes > 0 ? bookedMinutes / workingMinutes : null,
    level: occupancyLevel(workingMinutes, bookedMinutes),
  };
}

export function occupancyWeekForStaff(input: {
  days: Date[];
  workingHours: WorkingHours;
  appointments: OccupancyAppointment[];
  clinicianId: string;
}): OccupancyCell[] {
  return input.days.map((date) =>
    occupancyCellForDay({
      date,
      workingHours: input.workingHours,
      appointments: input.appointments,
      clinicianId: input.clinicianId,
    }),
  );
}

export function workingHoursToDayMap(
  hours: WorkingHours,
): Map<number, WeekDayHours | null> {
  const map = new Map<number, WeekDayHours | null>();
  WORKING_HOURS_DAY_KEYS.forEach((key, dayOfWeek) => {
    map.set(dayOfWeek, hours[key] ?? null);
  });
  return map;
}
